import { Injectable } from '@angular/core';
import { map, Observable, switchMap, combineLatestWith } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { IProfileLink } from '@shared/models/links';
import { IOrganization } from '@shared/models/organization';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectAuthUser } from '@state/auth/auth.selectors';
import {
  selectLastOrganizationUrlSlug,
  selectOrganizationDetails,
  selectLastOrganizationDetails,
} from '@state/organization/organization.selectors';

import { environment } from 'src/environments/environment';

const investorFinderUrl =
  environment.api + '/external/disposition/investor_finder/transactions';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class ProfileLinksService {
  constructor(private store: Store<AppState>) {}

  getProfileLinks(): Observable<IProfileLink[]> {
    return this.store
      .select(selectLastOrganizationUrlSlug)
      .pipe(untilDestroyed(this))
      .pipe(
        combineLatestWith(
          this.store.select(selectOrganizationDetails),
          this.store.select(selectLastOrganizationDetails)
        ),
        map(([lastOrgSlug, organizationDetails, lastOrganizationDetails]) => {
          return {
            lastOrgSlug,
            organizationDetails,
            lastOrganizationDetails,
          };
        }),
        switchMap(
          ({ lastOrgSlug, organizationDetails, lastOrganizationDetails }) => {
            return this.store.select(selectAuthUser).pipe(
              map(
                (user) => {
                  const lastOrg: IOrganization | null =
                    lastOrgSlug &&
                    lastOrgSlug ===
                      organizationDetails?.shortName?.toLowerCase()
                      ? organizationDetails
                      : lastOrgSlug &&
                        lastOrgSlug ===
                          lastOrganizationDetails?.shortName?.toLowerCase()
                      ? lastOrganizationDetails
                      : null;

                  return [
                    {
                      title: 'Rebuilt',
                      path: null,
                      icon: 'featherHome',
                      children: [
                        {
                          title: 'All Deals',
                          path: '/',
                        },
                        {
                          title: 'Buyer Services',
                          path: '/buy/services',
                        },
                        {
                          title: 'Disposition Services',
                          path: '/sell/services',
                        },
                      ],
                    },
                    {
                      title: 'Buy',
                      path: null,
                      icon: 'featherShoppingBag',
                      children: [
                        ...(lastOrg && lastOrg.id !== user?.organization?.id
                          ? [
                              {
                                title: `${lastOrg.name}`,
                                path: `/o/${lastOrg.shortName}`.toLowerCase(),
                              },
                            ]
                          : []),
                        { title: 'Offers', path: '/profile/offers' },
                        {
                          title: 'Favorites',
                          path: '/profile/favorites',
                        },
                        {
                          title: 'Buy Boxes',
                          path: '/buy-boxes',
                          featureFlag: 'fe_buy_boxes',
                        },
                      ],
                    },
                    {
                      title: 'Sell',
                      path: null,
                      icon: 'featherDollarSign',
                      children: [
                        ...(user?.organization
                          ? [
                              {
                                title: 'My Marketplace',
                                path: `/o/${user?.organization?.shortName?.toLowerCase()}`,
                                badge: 'Share',
                              },
                            ]
                          : []),
                        {
                          title: 'My Deals',
                          path: '/my-deals',
                        },
                        {
                          title: 'Upload Buyer List',
                          path: '/upload-buyers',
                          forceReload: true,
                          pathsToCheck: [
                            '/manage-buyers-lists',
                            '/manage-buyers-list',
                          ],
                        },
                        {
                          title: 'Investor Finder',
                          path: investorFinderUrl,
                          iframe: true,
                          badge: 'New',
                        },
                      ],
                    },
                    {
                      title: 'Account',
                      path: null,
                      icon: 'featherUser',
                      children: [
                        {
                          title: 'Manage Organization',
                          path: '/my-organization',
                        },
                        {
                          title: 'My Orders',
                          path: '/my-orders',
                          badge: 'New',
                        },
                        {
                          title: 'My Messages',
                          path: '/my-messages',
                          badge: 'New',
                        },
                        {
                          title: 'Manage Profile',
                          path: '/profile/account',
                        },
                        {
                          title: 'Logout',
                          path: '/logout',
                        },
                      ],
                    },
                  ];
                }
                // {
                //   title: 'UI Docs',
                //   path: null,
                //   icon: 'featherFileText',
                //   featureFlag: 'fe_ui_docs',
                //   removeFromProduction: true,
                //   children: [
                //     {
                //       title: 'Button',
                //       path: '/ui-docs/button',
                //     },
                //     {
                //       title: 'Colors',
                //       path: '/ui-docs/colors',
                //     },
                //     {
                //       title: 'Multi Selector',
                //       path: '/ui-docs/multi-selector',
                //     },
                //   ],
                // },
              )
            );
          }
        )
      );
  }
}
